import ITemplate from 'lib/Interfaces/ITemplate';

/**
 * Applies auto-translated descriptions and syncs language-independent fields
 * from English to all other language templates.
 *
 * @param templates - Array of templates for all languages
 * @param index - Index of the storage disclosure being translated
 * @param translations - Object mapping language codes to translated descriptions
 * @returns Updated templates with translations and synced fields
 */
export const applyAutoTranslateToStorage = (
  templates: ITemplate[],
  index: number,
  translations: Partial<Record<string, string>>,
): ITemplate[] => {
  // Find the English template to use as source for language-independent fields
  const engTemplate = templates.find((t) => t.language === 'en');

  if (!engTemplate) {
    return templates;
  }

  // Get the specific storage disclosure from English version
  const engStorage = engTemplate.deviceStorage.disclosures[index];

  if (!engStorage) {
    return templates;
  }

  // Update each template
  const updatedTemplates: ITemplate[] = [];

  for (const template of templates) {
    // Get translated description for this language
    const translatedDescription =
      translations[template.language] ?? template.deviceStorage.disclosures[index]?.description ?? '';

    // Update all storage disclosures in this template
    const updatedDisclosures = template.deviceStorage.disclosures.map((storage, storageIndex) => {
      // Only update the disclosure at the specified index
      if (storageIndex !== index) {
        return storage;
      }

      // Copy language-independent fields from English and set translated description
      return {
        ...storage,
        description: translatedDescription,
        name: engStorage.name,
        identifier: engStorage.identifier,
        type: engStorage.type,
        maxAgeSeconds: engStorage.maxAgeSeconds,
        domain: engStorage.domain,
      };
    });

    // Create updated template with new disclosures
    const updatedTemplate: ITemplate = {
      ...template,
      deviceStorage: {
        ...template.deviceStorage,
        disclosures: updatedDisclosures,
      },
    };

    updatedTemplates.push(updatedTemplate);
  }

  return updatedTemplates;
};
